<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue'
import { z } from 'zod'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { usePagination } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { VServerInfo, type VServerStatus } from '@/types/dashboard/vserver/VServerInfo'
import type { PaginatedResponse } from '@/types'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import { vserverStatusClass } from '@/utils/vserverStatus'
import { formatTimestampSeconds } from '@/utils'

const { t } = useI18n()
const router = useRouter()

const loading = ref(true)
const servers = ref<VServerInfo[]>([])
const statusFilter = ref<VServerStatus | 'all'>('all')
const searchQuery = ref('')
const searchDebounce = ref<ReturnType<typeof setTimeout> | null>(null)

const pagination = usePagination({ initialItemsPerPage: 50 })

const statusValues: Array<VServerStatus | 'all'> = [
  'all', 'active', 'stopped', 'provisioning', 'failed',
  'resizing', 'reinstalling', 'suspended', 'deprovisioning', 'deprovisioned'
]

const statusOptions = computed(() =>
  statusValues.map((v) => ({
    value: v,
    title: v === 'all'
      ? t('dashboard.vserver.admin.statusAll')
      : t(`dashboard.vserver.status.${v}`)
  }))
)

async function fetchServers() {
  loading.value = true
  const { limit, offset } = pagination.buildQueryParams()
  let url = `/admin/vserver/servers?limit=${limit}&offset=${offset}`
  if (statusFilter.value !== 'all') url += `&status=${statusFilter.value}`
  if (searchQuery.value.trim()) url += `&search=${encodeURIComponent(searchQuery.value.trim())}`

  try {
    const res = await useAxios<null, PaginatedResponse<VServerInfo>>('GET', url, true)
    if (res?.status === STATUS_CODES.OK && res.data) {
      const parsed = z.array(VServerInfo).safeParse(res.data.data)
      servers.value = parsed.success
        ? parsed.data
        : Array.isArray(res.data.data)
          ? (res.data.data as VServerInfo[])
          : []
      pagination.setTotal(res.data.total ?? 0)
    }
  } catch (err) {
    handleAxiosError(err, { logPrefix: 'AdminVServerHome' })
  } finally {
    loading.value = false
  }
}

watch(() => pagination.page.value, fetchServers)
watch(statusFilter, () => {
  pagination.goToPage(1)
  fetchServers()
})
watch(searchQuery, () => {
  if (searchDebounce.value) clearTimeout(searchDebounce.value)
  searchDebounce.value = setTimeout(() => {
    pagination.goToPage(1)
    fetchServers()
  }, 300)
})

const statusClass = vserverStatusClass

function openDetail(id: number) {
  router.push({ name: 'dashboardAdminVServerDetail', params: { id } })
}

function openIpPool() {
  router.push({ name: 'dashboardAdminVServerIpPool' })
}

function openImport() {
  router.push({ name: 'dashboardAdminVServerImport' })
}

function openTemplates() {
  router.push({ name: 'dashboardAdminVServerTemplates' })
}

function openSmtpRequests() {
  router.push({ name: 'dashboardAdminVServerSmtpRequests' })
}

onMounted(fetchServers)
</script>

<template>
  <BaseDashboardPage
    :title="t('dashboard.vserver.admin.title')"
    :subtitle="t('dashboard.vserver.admin.subtitle')"
    icon="mdi-server"
    icon-color="cyan"
  >
    <template #actions>
      <button class="btn-text" @click="openImport">
        <v-icon icon="mdi-import" size="16" />
        {{ $t('dashboard.vserver.admin.import.title') }}
      </button>
      <button class="btn-text" @click="openTemplates">
        <v-icon icon="mdi-disc" size="16" />
        {{ $t('dashboard.vserver.admin.templates.title') }}
      </button>
      <button class="btn-text" @click="openSmtpRequests">
        <v-icon icon="mdi-email-lock-outline" size="16" />
        {{ $t('dashboard.vserver.admin.smtp.title') }}
      </button>
      <button class="btn-text" @click="openIpPool">
        <v-icon icon="mdi-ip-network" size="16" />
        {{ $t('dashboard.vserver.admin.ipPool.title') }}
      </button>
    </template>

    <div class="filters-bar">
      <v-text-field
        v-model="searchQuery"
        prepend-inner-icon="mdi-magnify"
        :placeholder="$t('dashboard.vserver.admin.searchPlaceholder')"
        variant="outlined"
        density="compact"
        hide-details
        class="search-input"
      />
      <div class="status-filters">
        <button
          v-for="opt in statusOptions"
          :key="opt.value"
          class="filter-chip"
          :class="{ active: statusFilter === opt.value }"
          @click="statusFilter = opt.value"
        >
          {{ opt.title }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="state-box">
      <v-progress-circular indeterminate size="32" width="3" color="#00B0FF" />
    </div>

    <div v-else-if="servers.length === 0" class="state-box">
      <v-icon icon="mdi-server-network-off" size="48" class="empty-icon" />
      <p class="empty-title">{{ $t('dashboard.vserver.admin.empty.title') }}</p>
      <p class="empty-hint">{{ $t('dashboard.vserver.admin.empty.description') }}</p>
    </div>

    <div v-else class="section-card">
      <div class="data-table">
        <div class="table-header admin-grid">
          <span>{{ $t('dashboard.vserver.admin.table.hostname') }}</span>
          <span>{{ $t('dashboard.vserver.admin.table.status') }}</span>
          <span>{{ $t('dashboard.vserver.admin.table.ipv4') }}</span>
          <span>{{ $t('dashboard.vserver.admin.table.plan') }}</span>
          <span>{{ $t('dashboard.vserver.admin.table.node') }}</span>
          <span>{{ $t('dashboard.vserver.admin.table.createdAt') }}</span>
        </div>
        <div
          v-for="srv in servers"
          :key="srv.id"
          class="table-row admin-grid clickable"
          @click="openDetail(srv.id)"
        >
          <span class="mono">{{ srv.hostname }}</span>
          <span>
            <span class="status-chip" :class="statusClass(srv.status)">
              {{ $t(`dashboard.vserver.status.${srv.status}`) }}
            </span>
          </span>
          <span class="mono">{{ srv.ipv4_address ?? '-' }}</span>
          <span>{{ srv.plan_slug }}</span>
          <span class="mono">{{ srv.proxmox_node ?? '-' }}</span>
          <span class="text-dimmed">{{ formatTimestampSeconds(srv.created_at) }}</span>
        </div>
      </div>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.filters-bar {
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.search-input {
  min-width: 240px;
  max-width: 360px;
  flex: 1;
}

.status-filters {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.filter-chip {
  padding: 6px 12px;
  background: rgba(120, 163, 255, 0.06);
  border: 1px solid rgba(120, 163, 255, 0.12);
  border-radius: 999px;
  color: rgba(200, 218, 255, 0.7);
  font-size: 0.78rem;
  cursor: pointer;

  &.active {
    background: rgba(0, 176, 255, 0.15);
    border-color: rgba(0, 176, 255, 0.35);
    color: #00B0FF;
  }
}

.section-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 18px;
}

.data-table {
  overflow-x: auto;
}

.admin-grid {
  grid-template-columns: 1.4fr 0.8fr 1.1fr 1fr 0.7fr 0.8fr;
  min-width: 820px;
}

.table-header, .table-row {
  display: grid;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
}

.table-header {
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  transition: background 0.15s ease;
  cursor: pointer;

  &:hover {
    background: rgba(120, 163, 255, 0.04);
  }
}

.mono {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.85rem;
}

.text-dimmed {
  color: rgba(200, 218, 255, 0.5);
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  text-align: center;
}

.empty-icon {
  color: rgba(200, 218, 255, 0.25);
  margin-bottom: 12px;
}

.empty-title {
  font-size: 1rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.6);
  margin: 0 0 4px;
}

.empty-hint {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.45);
}

.btn-text {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.85);
  font-size: 0.82rem;
  cursor: pointer;
}

.status-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;
}

.status-success {
  background: rgba(129, 199, 132, 0.1);
  border: 1px solid rgba(129, 199, 132, 0.3);
  color: #81c784;
}

.status-error {
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  color: #ef5350;
}

.status-info {
  background: rgba(0, 176, 255, 0.1);
  border: 1px solid rgba(0, 176, 255, 0.3);
  color: #4fc3f7;
}

.status-warning {
  background: rgba(255, 183, 77, 0.1);
  border: 1px solid rgba(255, 183, 77, 0.3);
  color: #ffb74d;
}

.status-grey {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(200, 218, 255, 0.5);
}
</style>
